

// export default FloatingPanel;
import React, { useState, useEffect } from 'react';
import { Plus, Minus } from 'lucide-react';
import PanelContent from './PanelContent';

const FloatingPanel = ({ 

  onRegisterReset,
  width = '450px',
  children,
  onClose,
  onDateRangeChange,
  dateRange,
  imageryData,
  onImageSelect,
  selectedImages,
  onImageHover,
  onImageVisibilityToggle,
  onSatelliteVisibilityToggle,
  visibleImage,
  onCheckOut,
  onSaveDraft,
  drawnCoordinates,
  setDrawnCoordinates,
  loading,
  onFootprintSelect,
  onSaveAOI,
  onAOISelect,
  onTabSwitch,
  onTaskingDataUpdate,
  onSearch,
  isDrawing,
  isRestoringTaskingState,
  cachedTaskingState,
  onTaskingStateReady,
  onFeasibilityTriggerReady,
  onClearSelectionsReady
}) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const [activeTab, setActiveTab] = useState('');
  const [contentVisible, setContentVisible] = useState(false);

  // Effect to handle content visibility with delay
  useEffect(() => {
    if (!isMinimized) {
      // When maximizing, set default tab to 'catalog' if no tab is selected
      if (!activeTab) {
        setActiveTab('catalog');
      }
      // Small delay before showing content
      const timer = setTimeout(() => {
        setContentVisible(true);
      }, 50);
      return () => clearTimeout(timer);
    } else {
      setContentVisible(false);
      // When minimizing, clear the active tab after content fade out
      setTimeout(() => {
        setActiveTab('');
      }, 150);
    }
  }, [isMinimized, activeTab]);

  const baseStyles = {
    position: 'fixed',
    backgroundColor: 'hsl(0,0%,0%)',
    color: 'white',
    backdropFilter: 'blur(3px)',
    borderRadius: '10px',
    zIndex: 9999,
    fontFamily: 'Inter, system-ui, sans-serif',
    top: '50px',
    right: '5px',
    width: width,
    height: isMinimized ? '40px' : 'calc(100vh - 60px)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  };

  const handleTabClick = (tab) => {
    // If switching to any tab other than AOIs, clear non-AOI layers
    if (tab !== 'aois' && typeof onTabSwitch === 'function') {
      onTabSwitch(tab);
    }
    
    if (isMinimized) {
      setIsMinimized(false);
      setActiveTab(tab);
      console.log(tab)
    } else if (activeTab === tab) {
      return;
    } else {
      // Fade out current content before changing tab
      setContentVisible(false);
      setTimeout(() => {
        setActiveTab(tab);
        setContentVisible(true);
      }, 150);
    }
  };
  

  const handleToggle = () => {
    if (!isMinimized) {
      // Fade out content before minimizing
      setContentVisible(false);
      setTimeout(() => {
        setIsMinimized(true);
      }, 150);
    } else {
      setIsMinimized(false);
    }
  };

  const TabButton = ({ name, id }) => (
    <div style={{
      position: 'relative',
      padding: '0 4px 6px 4px',
      marginBottom: '-6px'
    }}>
      <button
        onClick={() => handleTabClick(id)}
        style={{
          color: activeTab === id ? '#B9CEF0' : '#6A9EF5',
          fontSize: '15px',
          fontWeight: activeTab === id ? '500' : '400',
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          position: 'relative',
          transition: 'all 0.3s ease'
        }}
      >
        {name}
      </button>
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        width: '100%',
        height: '4px',
        background: '#B9CEF0',
        opacity: activeTab === id ? 1 : 0,
        transition: 'all 0.3s ease',
        borderRadius: '2px 2px 0 0',
        transform: `scaleX(${activeTab === id ? 1 : 0})`,
        transformOrigin: 'center'
      }} />
    </div>
  );

  return (
    <div style={baseStyles}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 16px 4px 16px',
        borderBottom: isMinimized ? 'none' : '1px solid rgba(255, 255, 255, 0.1)',
        transition: 'border-bottom 0.3s ease'
      }}>
        <div style={{ display: 'flex', gap: '24px' }}>
          <TabButton name="Catalog" id="catalog" />
          <TabButton name="Task" id="task" />
          <TabButton name="My AOIs" id="aois" />
        </div>
        
        <button
          onClick={handleToggle}
          style={{
            color: 'rgba(255, 255, 255, 0.5)',
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '18px',
            height: '18px',
            transition: 'transform 0.3s ease'
          }}
        >
          {isMinimized ? <Plus size={18} /> : <Minus size={18} />}
        </button>
      </div>

      <div style={{
        flex: 1,
        opacity: contentVisible ? 1 : 0,
        transform: `translateY(${contentVisible ? 0 : '10px'})`,
        transition: 'opacity 0.3s ease, transform 0.3s ease',
        display: isMinimized ? 'none' : 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {activeTab && (
          <PanelContent 
            onRegisterReset={onRegisterReset}
            activeTab={activeTab}
            onDateRangeChange={onDateRangeChange}
            dateRange={dateRange}
            imageryData={imageryData}
            onImageSelect={onImageSelect}
            selectedImages={selectedImages}
            onImageHover={onImageHover}
            onImageVisibilityToggle={onImageVisibilityToggle}
            onSatelliteVisibilityToggle={onSatelliteVisibilityToggle}
            visibleImage={visibleImage}
            onCheckOut={onCheckOut}
            onSaveDraft={onSaveDraft}
            drawnCoordinates={drawnCoordinates}
            setDrawnCoordinates={setDrawnCoordinates}
            loading={loading}
            onFootprintSelect={onFootprintSelect}
            onSaveAOI={onSaveAOI}
            onAOISelect={(coordinates, name) => {
              onAOISelect(coordinates, name);  // Pass both parameters
            }}
            onTaskingDataUpdate={onTaskingDataUpdate}
            onSearch={onSearch}
            isDrawing={isDrawing}
            isRestoringTaskingState={isRestoringTaskingState}
            cachedTaskingState={cachedTaskingState}
            onTaskingStateReady={onTaskingStateReady}
            onFeasibilityTriggerReady={onFeasibilityTriggerReady}
            onClearSelectionsReady={onClearSelectionsReady}
          />
        )}
      </div>
    </div>
  );
};



export default FloatingPanel;




